<!--
  PLACEHOLDER — Income Estimate (demo)

  Custom inline-SVG stacked bar chart for the Income Estimate page. We do
  NOT use the canonical @/common/StackedBarChart.vue (Chart.js / canvas)
  here because the design requires 45° hatched fills on the "estimated"
  portion of each stack, which Chart.js cannot natively reproduce — SVG
  <pattern> is the simplest faithful render. When this feature ships for
  real, evaluate whether hatched fills are still required and consider
  consolidating onto StackedBarChart if not.

  All data wiring is currently hardcoded — see incomeEstimateDemoData.ts.
-->
<template>
  <svg
    :viewBox="`0 0 ${W} ${H}`"
    class="classic-chart"
    role="img"
    preserveAspectRatio="xMidYMid meet"
  >
    <defs>
      <pattern
        v-for="cat in CATEGORIES"
        :key="cat"
        :id="hatchId(cat)"
        patternUnits="userSpaceOnUse"
        :width="HATCH_DENSITY"
        :height="HATCH_DENSITY"
        patternTransform="rotate(-45)"
      >
        <rect
          :width="HATCH_DENSITY"
          :height="HATCH_DENSITY"
          :fill="CATEGORY_COLORS[cat]"
        />
        <line
          :x1="0"
          :y1="0"
          :x2="0"
          :y2="HATCH_DENSITY"
          stroke="rgba(255,255,255,0.5)"
          stroke-width="2"
        />
      </pattern>
    </defs>

    <!-- Gridlines + y-axis ticks -->
    <g v-for="v in gridLines" :key="v">
      <line
        :x1="padL"
        :x2="W - padR"
        :y1="yScale(v)"
        :y2="yScale(v)"
        :class="['grid-line', v === 0 ? 'grid-line--zero' : '']"
      />
      <text :x="padL - 8" :y="yScale(v) + 3.5" text-anchor="end" class="y-tick">
        €{{ fmtK(v) }}
      </text>
    </g>
    <text :x="padL - 8" :y="padT - 8" text-anchor="end" class="y-axis-title">
      Net €
    </text>

    <!-- Year band labels + year dividers -->
    <g v-for="(g, gi) in groups" :key="g.year">
      <line
        :x1="padL + g.start * bw + 4"
        :x2="padL + (g.end + 1) * bw - 4"
        :y1="H - padB + 38 - 11"
        :y2="H - padB + 38 - 11"
        class="year-band-rule"
      />
      <text
        :x="(padL + g.start * bw + padL + (g.end + 1) * bw) / 2"
        :y="H - padB + 38"
        text-anchor="middle"
        class="year-band-label"
      >
        20{{ g.year }}
      </text>
      <line
        v-if="gi > 0"
        :x1="padL + g.start * bw"
        :x2="padL + g.start * bw"
        :y1="padT"
        :y2="padT + innerH"
        class="year-divider"
      />
    </g>

    <!-- Stacked bars -->
    <g v-for="(m, i) in monthSlice" :key="m.key">
      <template v-for="seg in barSegments(i)" :key="seg.key">
        <rect
          :x="seg.x"
          :y="seg.y"
          :width="seg.width"
          :height="seg.height"
          :fill="seg.fill"
        />
      </template>
      <rect
        :x="padL + i * bw"
        :y="padT"
        :width="bw"
        :height="innerH"
        fill="transparent"
        @mouseenter="hover = i"
        @mouseleave="hover = null"
      />
      <rect
        v-if="hover === i"
        :x="padL + i * bw"
        :y="padT"
        :width="bw"
        :height="innerH"
        fill="rgba(31,77,51,0.05)"
        pointer-events="none"
      />
      <text
        :x="padL + i * bw + barInset + barWidth / 2"
        :y="H - padB + 14"
        text-anchor="middle"
        class="month-label"
      >
        {{ m.short }}
      </text>
    </g>

    <!-- Tooltip -->
    <g v-if="tooltip" class="chart-tooltip" pointer-events="none">
      <line
        :x1="tooltip.cx"
        :x2="tooltip.cx"
        :y1="padT"
        :y2="padT + innerH"
        class="chart-tt-needle"
      />
      <rect
        :x="tooltip.x"
        :y="tooltip.y"
        :width="tooltip.w"
        :height="tooltip.h"
        :rx="5"
        class="chart-tt-bg"
      />
      <text :x="tooltip.x + 11" :y="tooltip.y + 16" class="chart-tt-month">
        {{ tooltip.monthLabel }}
      </text>
      <g v-for="(row, idx) in tooltip.rows" :key="row.cat">
        <rect
          :x="tooltip.x + 11"
          :y="tooltip.y + 30 + idx * 14 - 7"
          :width="8"
          :height="8"
          :fill="CATEGORY_COLORS[row.cat]"
          :rx="1"
        />
        <text
          :x="tooltip.x + 24"
          :y="tooltip.y + 30 + idx * 14"
          class="chart-tt-row"
        >
          {{ row.cat }}
        </text>
        <text
          :x="tooltip.x + tooltip.w - 11"
          :y="tooltip.y + 30 + idx * 14"
          text-anchor="end"
          class="chart-tt-row"
        >
          €{{ fmt(row.value) }}
        </text>
      </g>
      <line
        :x1="tooltip.x + 11"
        :x2="tooltip.x + tooltip.w - 11"
        :y1="tooltip.y + 78"
        :y2="tooltip.y + 78"
        class="chart-tt-divider"
      />
      <text :x="tooltip.x + 11" :y="tooltip.y + 92" class="chart-tt-total">
        Net total
      </text>
      <text
        :x="tooltip.x + tooltip.w - 11"
        :y="tooltip.y + 92"
        text-anchor="end"
        class="chart-tt-total"
      >
        €{{ fmt(tooltip.total) }}
      </text>
    </g>
  </svg>
</template>

<script setup lang="ts">
import { computed } from "vue";
import {
  CATEGORIES,
  CATEGORY_COLORS,
  categoryMonthly,
  categoryVariableMonthly,
  fmt,
  fmtK,
  MONTHS,
  monthsForHorizon,
  type Category,
  type HorizonValue,
  type Stream,
} from "./incomeEstimateDemoData";

const props = defineProps<{
  horizon: HorizonValue;
  streams: Stream[];
}>();

const hover = defineModel<number | null>("hover", { required: true });

// Geometry — matches the design's StackedChartV1.
const W = 880;
const H = 360;
const padL = 56;
const padR = 12;
const padT = 16;
const padB = 54;
const innerW = W - padL - padR;
const innerH = H - padT - padB;
const HATCH_DENSITY = 5;

const hatchId = (cat: Category) => `hatch-${cat.toLowerCase()}`;

const monthCount = computed(() => monthsForHorizon(props.horizon));
const monthSlice = computed(() => MONTHS.slice(0, monthCount.value));
const bw = computed(() => innerW / monthCount.value);
const barInset = computed(() => bw.value * 0.18);
const barWidth = computed(() => bw.value * 0.64);

const cm = computed(() => categoryMonthly(props.streams));
const vm = computed(() => categoryVariableMonthly(props.streams));

const monthlyTotals = computed(() =>
  monthSlice.value.map((_m, i) =>
    CATEGORIES.reduce((s, c) => s + cm.value[c][i], 0)
  )
);

const yMax = computed(() => {
  const dataMax = Math.max(...monthlyTotals.value, 1);
  const yStep = dataMax > 50000 ? 25000 : dataMax > 20000 ? 10000 : 5000;
  return Math.ceil(dataMax / yStep) * yStep;
});

const gridLines = computed(() => {
  const dataMax = Math.max(...monthlyTotals.value, 1);
  const yStep = dataMax > 50000 ? 25000 : dataMax > 20000 ? 10000 : 5000;
  const out: number[] = [];
  for (let v = 0; v <= yMax.value; v += yStep) out.push(v);
  return out;
});

function yScale(v: number) {
  return padT + innerH - (v / yMax.value) * innerH;
}

interface YearGroup {
  year: string;
  start: number;
  end: number;
  count: number;
}

const groups = computed<YearGroup[]>(() => {
  const acc: YearGroup[] = [];
  monthSlice.value.forEach((m, i) => {
    const last = acc[acc.length - 1];
    if (last && last.year === m.year) {
      last.count++;
      last.end = i;
    } else {
      acc.push({ year: m.year, start: i, end: i, count: 1 });
    }
  });
  return acc;
});

interface BarSeg {
  key: string;
  x: number;
  y: number;
  width: number;
  height: number;
  fill: string;
}

function barSegments(i: number): BarSeg[] {
  const x = padL + i * bw.value + barInset.value;
  let bottom = 0;
  const segs: BarSeg[] = [];
  for (const cat of CATEGORIES) {
    const total = cm.value[cat][i];
    const variable = vm.value[cat][i];
    const fixed = total - variable;
    if (total < 1) continue;
    const fixedH = (fixed / yMax.value) * innerH;
    const variableH = (variable / yMax.value) * innerH;
    if (fixed > 0) {
      segs.push({
        key: `${cat}-fix-${i}`,
        x,
        y: padT + innerH - bottom - fixedH,
        width: barWidth.value,
        height: fixedH,
        fill: CATEGORY_COLORS[cat],
      });
      bottom += fixedH;
    }
    if (variable > 0) {
      segs.push({
        key: `${cat}-var-${i}`,
        x,
        y: padT + innerH - bottom - variableH,
        width: barWidth.value,
        height: variableH,
        fill: `url(#${hatchId(cat)})`,
      });
      bottom += variableH;
    }
  }
  return segs;
}

interface TooltipShape {
  cx: number;
  x: number;
  y: number;
  w: number;
  h: number;
  monthLabel: string;
  rows: { cat: Category; value: number }[];
  total: number;
}

const tooltip = computed<TooltipShape | null>(() => {
  if (hover.value === null) return null;
  const i = hover.value;
  const m = monthSlice.value[i];
  if (!m) return null;
  const total = monthlyTotals.value[i];
  const cx = padL + i * bw.value + bw.value / 2;
  const w = 156;
  const h = 102;
  let x = cx - w / 2;
  if (x < padL + 2) x = padL + 2;
  if (x + w > W - padR - 2) x = W - padR - 2 - w;
  let y = yScale(total) - h - 10;
  if (y < padT + 2) y = padT + 2;
  return {
    cx,
    x,
    y,
    w,
    h,
    monthLabel: `${m.short.toUpperCase()} 20${m.year}`,
    rows: CATEGORIES.map((cat) => ({ cat, value: cm.value[cat][i] })),
    total,
  };
});
</script>

<style scoped lang="scss">
/* SVG-internal styling — gridlines, axis ticks, year dividers, and the
   bar-hover tooltip. None of this has a Bootstrap analogue. */
.classic-chart {
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
  display: block;
  height: auto;

  .grid-line {
    stroke: var(--phoenix-gray-200);
    stroke-width: 1;
  }
  .grid-line--zero {
    stroke: var(--phoenix-gray-400);
  }
  .y-tick {
    font-size: 10px;
    fill: var(--phoenix-tertiary-color);
    font-variant-numeric: tabular-nums;
  }
  .month-label {
    font-size: 10px;
    fill: var(--phoenix-tertiary-color);
  }
  .y-axis-title {
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    fill: var(--phoenix-gray-500);
  }
  .year-band-label {
    font-size: 9.5px;
    font-weight: 500;
    fill: var(--phoenix-gray-500);
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.08em;
  }
  .year-band-rule {
    stroke: var(--phoenix-gray-200);
    stroke-width: 1;
  }
  .year-divider {
    stroke: var(--phoenix-gray-400);
    stroke-dasharray: 3 3;
  }
  .chart-tt-needle {
    stroke: var(--phoenix-gray-1100);
    stroke-width: 1;
    stroke-dasharray: 2 2;
    opacity: 0.5;
  }
  .chart-tt-bg {
    fill: var(--phoenix-gray-1100);
    opacity: 0.96;
  }
  .chart-tt-month {
    font-size: 9px;
    fill: rgba(255, 255, 255, 0.55);
    letter-spacing: 0.12em;
    text-transform: uppercase;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
  .chart-tt-row {
    font-size: 11px;
    fill: #fff;
    font-variant-numeric: tabular-nums;
  }
  .chart-tt-total {
    font-size: 11.5px;
    fill: #fff;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
  .chart-tt-divider {
    stroke: rgba(255, 255, 255, 0.18);
    stroke-width: 1;
  }
}
</style>
